import React, { Component } from 'react';
import { FormicaProps, RouteProps, withFormica } from '../../../../../helpers/withFormica';
import { RootState } from '../../../../../redux/store';
import { ConfigurationState } from '../../../../../redux/reducers/ConfigurationReducer';
import { changeConfiguration } from '../../../../../redux/actions/ConfigurationActions';
import { changePageFilter } from '../../../../../redux/actions/PageActions';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import FormicaSearchSelectBox from '../../../../custom/FormicaSearchSelectBox';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../../../config/binding/InstanceBinding';
import Injectables from '../../../../../constant/Injectables';
import CollectionNames from '../../../../../constant/CollectionNames';
import { TransactionJsonModel } from '../../../../../domain/stream/transaction/TransactionDomains';
import Icon from 'icons/Icon';
import { CaseAdditionalChannel } from '../../../../../domain/case/CaseDomains';
import { customConfirm } from '../../../../custom/CustomPopupConfirm';
import MessageUtil from '../../../../../util/MessageUtil';
import DataType from '../../../../../constant/DataType';
import ComponentName from '../../../../../constant/ComponentName';
import LanguageUtil from '../../../../../util/LanguageUtil';
import SubscriptionContext = subscribe.SubscriptionContext;
import Computation = subscribe.Computation;
import QueryableRepository = repository.QueryableRepository;
import QueryBuilder = query.QueryBuilder;

class DomainConfigurationAdditionalChannelComponent extends Component<Props, State> {
  public state: State = new State();

  private readonly subscriptionContext: SubscriptionContext = InstanceContainer.get(Injectables.SUBSCRIPTION_CONTEXT);
  private computation: Computation | undefined;

  private readonly repository: QueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render() {
    const { selectedChannel, selectedField, selectedKeyField } = this.state;
    const {
      configuration: { configuration },
    } = this.props;
    return (
      <div className='w-full flex flex-col gap-3'>
        <div className='w-full flex gap-2'>
          <FormicaSearchSelectBox
            options={this.getChannelOptions()}
            value={selectedChannel}
            onChange={(value) => this.setState({ selectedChannel: value.value })}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.selectChannel', defaultMessage: 'Select Channel' })}
            data-testid={`${ComponentName.CONFIGURATION}_additional_channel_creator_channel_select`}
          />
          <FormicaSearchSelectBox
            options={this.getChannelFieldOptions(selectedChannel)}
            value={selectedField}
            onChange={(value) => this.setState({ selectedField: value.value })}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.selectField', defaultMessage: 'Select Field' })}
            data-testid={`${ComponentName.CONFIGURATION}_additional_channel_creator_field_select`}
          />
          <FormicaSearchSelectBox
            options={this.getKeyFieldOptions()}
            value={selectedKeyField}
            onChange={(value) => this.setState({ selectedKeyField: value.value })}
            placeholder={LanguageUtil.get({ id: 'common.input.placeholder.keyField', defaultMessage: 'Key Field (Optional)' })}
            showClear
            data-testid={`${ComponentName.CONFIGURATION}_additional_channel_creator_keyfield_select`}
          />
          <button
            className='bg-primary rounded min-w-[34px] h-[34px] flex justify-center items-center'
            onClick={this.addChannel}
            data-testid={`${ComponentName.CONFIGURATION}_additional_channel_creator_add_button`}
          >
            <Icon color='white' fill='white' size={18} icon='add' />
          </button>
        </div>

        <div className='flex flex-col gap-2'>
          {configuration?.additionalChannels?.map((channel, index) => (
            <div key={`${channel.channel}-${index}`} className='w-full flex gap-2'>
              <FormicaSearchSelectBox
                options={this.getChannelOptions()}
                value={channel.channel}
                disabled
                data-testid={`${ComponentName.CONFIGURATION}_additional_channel_${channel.channel}_channel_text`}
              />
              <FormicaSearchSelectBox
                options={this.getChannelFieldOptions(channel.channel)}
                value={channel.field}
                disabled
                data-testid={`${ComponentName.CONFIGURATION}_additional_channel_${channel.channel}_field_text`}
              />
              <FormicaSearchSelectBox
                options={this.getKeyFieldOptions()}
                value={channel.keyField}
                disabled
                placeholder={LanguageUtil.get({
                  id: 'common.input.placeholder.keyField',
                  defaultMessage: 'Key Field (Optional)',
                })}
                data-testid={`${ComponentName.CONFIGURATION}_additional_channel_${channel.channel}_keyfield_text`}
              />
              <button
                className='border rounded min-w-[34px] h-[34px] flex justify-center items-center'
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.removeChannel(e, channel)}
                data-testid={`${ComponentName.CONFIGURATION}_additional_channel_${channel.channel}_remove_button`}
              >
                <Icon color='#4F4F4F' fill='#4F4F4F' size={18} icon='delete' />
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  public componentDidMount() {
    const subscriptions = this.subscriptionContext.getSubscriptionsByCollection(CollectionNames.TRANSACTIONS);
    this.computation = subscribe.Tracker.autorun(this.tracker, subscriptions);
    this.loadChannelConfigurations();
  }

  public componentWillUnmount() {
    this.computation?.stop();
  }

  private tracker = async () => {
    const transactions = await this.repository.findMany<TransactionJsonModel>(QueryBuilder.select().from(CollectionNames.TRANSACTIONS).build());
    this.setState({ channels: transactions as TransactionJsonModel[] }, () => {
      this.loadChannelConfigurations();
    });
  };

  private getChannelOptions = () => {
    const { channels } = this.state;
    const {
      configuration: { configuration },
    } = this.props;

    const validChannels = channels.filter((channel) => {
      if (channel.id === configuration.dataSource) {
        return false;
      }
      return this.hasChannelConfiguration(channel.id);
    });

    return validChannels.map((channel) => ({
      label: channel.name,
      value: channel.id,
    }));
  };

  private hasChannelConfiguration = (channelId: string | undefined): boolean => {
    if (!channelId) return false;

    const { channelConfigurations } = this.state;

    if (channelConfigurations.has(channelId)) {
      return channelConfigurations.get(channelId) || false;
    }
    return false;
  };

  private loadChannelConfigurations = async () => {
    const { channels } = this.state;
    const { channelConfigurations } = this.state;

    try {
      const caseSettingsQuery = query.QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).build();

      const allCaseSettings = await this.repository.findMany(caseSettingsQuery);
      const configuredChannels = new Set(allCaseSettings.map((cs: any) => cs.dataSource));

      console.log('Configured channels:', Array.from(configuredChannels));

      for (const channel of channels) {
        if (channel.id) {
          const hasConfig = configuredChannels.has(channel.id);
          channelConfigurations.set(channel.id, hasConfig);
          console.log(`Channel ${channel.id} has config: ${hasConfig}`);
        }
      }

      this.setState({ channelConfigurations });
    } catch (error) {
      console.error('Channel configuration yükleme hatası:', error);
    }
  };

  private getChannelFieldOptions = (selectedChannel: string | undefined) => {
    const { channels } = this.state;
    if (selectedChannel === undefined) return [];
    const channel = channels.find((channel) => channel.id === selectedChannel);
    if (!channel) return [];
    return channel.variables
      .filter((v) => v.properties.dataType != DataType.STRUCTURE && v.properties.dataType != DataType.ARRAY)
      .map((field) => ({ label: field.name, value: field.name }));
  };

  private getKeyFieldOptions = () => {
    const { channels } = this.state;
    const {
      configuration: { configuration },
    } = this.props;

    if (!configuration?.dataSource) {
      return [];
    }

    const mainChannel = channels.find((channel) => channel.id === configuration.dataSource);
    if (!mainChannel) {
      return [];
    }

    return mainChannel.variables.filter((v) => v.properties.dataType === DataType.STRING).map((field) => ({ label: field.name, value: field.name }));
  };

  private addChannel = () => {
    const { selectedChannel, selectedField, selectedKeyField } = this.state;
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;

    if (!selectedChannel) {
      MessageUtil.error({ summary: LanguageUtil.get({ id: 'messages.error.error' }), detail: LanguageUtil.get({ id: 'messages.error.channel' }) });
      return;
    }
    if (!selectedField) {
      MessageUtil.error({ summary: LanguageUtil.get({ id: 'messages.error.error' }), detail: LanguageUtil.get({ id: 'messages.error.field' }) });
      return;
    }

    if (!this.hasChannelConfiguration(selectedChannel)) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'messages.error.error' }),
        detail: LanguageUtil.get({
          id: 'messages.error.channelNoConfiguration',
          defaultMessage: 'This channel has no configuration and cannot be added as additional channel.',
        }),
      });
      return;
    }

    const isExist = configuration.additionalChannels.find((channel) => channel.channel === selectedChannel);
    if (isExist) {
      MessageUtil.error({ summary: LanguageUtil.get({ id: 'messages.error.error' }), detail: LanguageUtil.get({ id: 'messages.error.channelAlready' }) });
      return;
    }

    const newChannel: CaseAdditionalChannel = {
      channel: selectedChannel,
      field: selectedField,
      keyField: selectedKeyField,
    };

    configuration.additionalChannels.push(newChannel);
    changeConfiguration(configuration);
    this.setState({ selectedChannel: undefined, selectedField: undefined, selectedKeyField: undefined });
  };

  private removeChannel = (target: React.MouseEvent<HTMLButtonElement>, channel: CaseAdditionalChannel) => {
    const {
      configuration: { configuration },
      changeConfiguration,
    } = this.props;
    customConfirm({
      target: target.currentTarget,
      accept: () => {
        const index = configuration.additionalChannels.indexOf(channel);
        if (index != -1) {
          configuration.additionalChannels.splice(index, 1);
          changeConfiguration(configuration);
        }
      },
      message: LanguageUtil.get({ id: 'messages.confirm.deleteChannel', defaultMessage: 'Do you sure delete this channel?' }),
    });
  };
}

interface Props extends RouteProps, FormicaProps, ReduxProps {}

const mapStateToProps = (state: RootState) => ({
  configuration: state.configuration as ConfigurationState,
  filter: state.page.filter,
});
const mapDispatchToProps = {
  changeConfiguration,
  changePageFilter,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  channels: TransactionJsonModel[] = [];
  selectedChannel: string | undefined;
  selectedField: string | undefined;
  selectedKeyField: string | undefined;
  channelConfigurations: Map<string, boolean> = new Map();
}

export default withFormica(connector(DomainConfigurationAdditionalChannelComponent));
